import { Component, inject, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AccountEndpoint } from '../../core/api/services/account-endpoint';

/** Set once the tour is skipped, so it is not shown again on this browser. */
export const TOUR_SKIPPED_KEY = 'tour_skipped';

interface TourStep {
  title: string;
  description: string;
}

@Component({
  selector: 'app-tour',
  imports: [ButtonModule],
  templateUrl: './tour.html'
})
export class Tour {
  private router = inject(Router);
  private accountEndpoint = inject(AccountEndpoint);

  readonly close = output<void>();

  readonly totalSteps = 4;
  readonly step = signal(0);

  // Preloaded on tour open so step transitions play instantly (see tour.html).
  readonly videoSources = [
    'videos/step_2.mp4',
    'videos/step_3.mp4',
    'videos/step_4.mp4',
  ];

  readonly steps: TourStep[] = [
    {
      title: 'Welcome to your loan portal',
      description:
        "This is where you track your deals, upload the documents your lender needs, and reach your loan team — all in one place. Here's a quick tour.",
    },
    {
      title: 'Start here: My deals',
      description:
        "Every deal you're part of shows up on your My deals list, along with its current stage. Starting a new project? Kick it off yourself with Submit deal.",
    },
    {
      title: "Upload what's requested",
      description:
        "Open a deal's Checklist to see exactly what to provide. Documents are grouped by party — Borrower, Guarantor, etc. — and the progress bar tracks what's left.",
    },
    {
      title: 'Track status & get help',
      description:
        'Each file is marked Approved, Under Review, or Rejected with a reason. Check Stage Progress for your loan status, or View team to contact us.',
    },
  ];

  next(): void {
    if (this.step() < this.totalSteps - 1) {
      this.step.update(s => s + 1);
    }
  }

  back(): void {
    if (this.step() > 0) {
      this.step.update(s => s - 1);
    }
  }

  onClose(): void {
    this.close.emit();
  }

  skip(): void {
    localStorage.setItem(TOUR_SKIPPED_KEY, 'true');
    this.close.emit();
  }

  goToDeals(): void {
    this.accountEndpoint.onboardTour().subscribe(() => {
      this.close.emit();
      this.router.navigate(['/portal/deals']);
    })
  }
}
